import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular/messages';
import { TranslateService } from '@ngx-translate/core';
import { catchError, concatMap, map, of, switchMap, tap } from 'rxjs';
import { finalizeLoader } from '@app/shared/components';
import { WarehouseError } from '@app/store/shared/models';
import { sortingActions } from '../actions/sorting.actions';
import { HttpService } from '../services/http.service';

const SCAN_ERROR_KEYS: Record<number, string> = {
    404: 'sorting.scan-product.error.not-found',
};

@Injectable()
export class SortingEffects {
    private readonly actions$ = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);

    scanProduct$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(sortingActions.scanProduct),
            switchMap(({ ean }) => {
                return this.httpService.scanProduct(ean).pipe(
                    finalizeLoader('sorting-scan-product'),
                    map((payload) => sortingActions.scanProductSuccess({ payload })),
                    catchError((error: HttpErrorResponse) => of(sortingActions.scanProductError({ code: error.status }))),
                );
            }),
        );
    });

    addItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(sortingActions.addItem),
            concatMap(({ cartonCode, ean }) => {
                return this.httpService.addItem(cartonCode, ean).pipe(
                    finalizeLoader('sorting-add-item'),
                    map((payload) => sortingActions.addItemSuccess({ payload })),
                    catchError((error: WarehouseError) => {
                        this.messageService.add(this.translateService.instant(error.key), 'danger');
                        return of(sortingActions.addItemError({ code: error.code, key: error.key }));
                    }),
                );
            }),
        );
    });

    scanProductError$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(sortingActions.scanProductError),
                tap(({ code }) => {
                    const key = SCAN_ERROR_KEYS[code] ?? 'sorting.scan-product.error.generic';
                    this.messageService.add(this.translateService.instant(key), 'danger');
                }),
            );
        },
        { dispatch: false },
    );

    addItemSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(sortingActions.addItemSuccess),
                tap(() => this.messageService.add(this.translateService.instant('sorting.add.success'), 'success')),
            );
        },
        { dispatch: false },
    );
}
